// Per-session JSON-path hints cache (Slice C4).
//
// JCS-9 mandates "no persistence to MetaInfo by default; users may save
// common paths" — so the runtime cache lives in-memory only, scoped to the
// app session. Persistence is a separate user action that writes to
// MetaInfo `pathHints`.
//
// Keyed by `(connectionId, database, schema, table, column)` folded
// lowercase for cross-engine consistency (Oracle's uppercase identifiers
// fold the same way through the dialog's display path — see C4 dialog).
// The cache stores both the discovered paths (with frequency) and the
// scan timestamp so consumers can render "Sampled at HH:MM" labels.

import { SAMPLE_PATH_CAP, type PathHintsCacheEntry, type SampledPath, type SampleTarget } from './types';

function key(connectionId: string, database: string, target: SampleTarget): string {
  return [
    connectionId,
    database,
    target.schema,
    target.table,
    target.column,
  ]
    .map((s) => s.toLowerCase())
    .join('|');
}

const STORE = new Map<string, PathHintsCacheEntry>();

export function setPathHints(
  connectionId: string,
  database: string,
  entry: PathHintsCacheEntry,
): void {
  STORE.set(key(connectionId, database, entry.target), entry);
}

export function getPathHints(
  connectionId: string,
  database: string,
  target: SampleTarget,
): PathHintsCacheEntry | undefined {
  return STORE.get(key(connectionId, database, target));
}

export function clearPathHints(
  connectionId: string,
  database: string,
  target: SampleTarget,
): void {
  STORE.delete(key(connectionId, database, target));
}

// Test seam — flush the entire cache between test runs.
export function _resetPathHintsCacheForTests(): void {
  STORE.clear();
}

// Merge MetaInfo-declared `pathHints` (string[] from `jsonStructureCache`)
// with the cache entry, preferring cache frequency when both have a path.
// Used by the completion-emit helper so MetaInfo entries become "frequency 0"
// candidates that still surface but rank below sampled hits.
export function mergeWithDeclaredHints(
  cacheEntry: PathHintsCacheEntry | undefined,
  declared: readonly string[] | undefined,
): readonly SampledPath[] {
  const out = new Map<string, number>();
  if (declared) {
    for (const p of declared) {
      if (p) out.set(p, 0);
    }
  }
  if (cacheEntry) {
    for (const p of cacheEntry.paths) {
      out.set(p.path, p.frequency);
    }
  }
  // Enforce JCS-9 ≤64 invariant at the source so a direct cache consumer
  // (not just emitJsonPathCandidates) cannot exceed it. Declared-only
  // (frequency 0) entries sort last and are therefore dropped first —
  // this mirrors the existing emitJsonPathCandidates slice; keep the two
  // in sync (a grep of either site should find the other).
  return [...out.entries()]
    .map(([path, frequency]) => ({ path, frequency }))
    .sort((a, b) => b.frequency - a.frequency || a.path.localeCompare(b.path))
    .slice(0, SAMPLE_PATH_CAP);
}
